import React, { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError, fmtDate } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ArrowLeft, WarningCircle, CheckCircle } from "@phosphor-icons/react";
import { DocChecklist } from "@/components/hr/DocChecklist";
import { FaceSetup } from "@/components/hrms/FaceSetup";
import { IncrementsSection } from "@/components/hr/Increments";
import { FamilyContacts } from "@/components/hr/FamilyContacts";
import { GenerateLetterButton, LetterActions } from "@/components/hr/JoiningLetter";
import { DocTypesDialog } from "@/components/hr/DocTypesDialog";
import { EmployeeDetailsForm, validateDetails } from "@/components/hr/EmployeeDetailsForm";
import { SalaryDetails } from "@/components/hr/SalaryDetails";

const DetailsEditor = ({ emp, onSaved }) => {
  const { user } = useAuth();
  const pick = (e) => ({ _existing: true, employee_code: e.employee_code || "", name: e.name || "", phone: e.phone || "", email: e.email || "", designation: e.designation || "", department: e.department || "", joining_date: e.joining_date || "", work_location: e.work_location || "", store_id: e.store_id || "", reporting_manager: e.reporting_manager || "", regional_manager: e.regional_manager || "", employment_type: e.employment_type || "Full-time" });
  const [f, setF] = useState(pick(emp));
  const [saving, setSaving] = useState(false);
  useEffect(() => { setF(pick(emp)); }, [emp]);
  const save = async () => {
    const er = validateDetails(f, user?.role);
    if (er) { toast.error(er); return; }
    setSaving(true);
    try { const { _existing, employee_code, ...payload } = f; await api.patch(`/salary/employees/${emp.id}`, payload); toast.success("Employee details saved"); onSaved(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };
  return (
    <div className="card-flat p-4 space-y-3" data-testid="hr-details-section">
      <EmployeeDetailsForm f={f} setF={setF} />
      <div className="flex justify-end"><Button onClick={save} disabled={saving} data-testid="hr-details-save">Save Details</Button></div>
    </div>
  );
};

const Section = ({ title, children, testid }) => (
  <section className="space-y-2" data-testid={testid}>
    <h2 className="text-base md:text-lg font-heading font-semibold">{title}</h2>
    {children}
  </section>
);

const Blockers = ({ blockers }) => (
  blockers.length ? (
    <div className="card-flat p-3 border-amber-300 bg-amber-50 text-[12.5px]" data-testid="hr-blockers">
      <div className="flex items-center gap-1.5 font-medium text-amber-800"><WarningCircle size={15} /> Joining Letter blocked until:</div>
      <ul className="list-disc ml-6 mt-1 text-amber-900">{blockers.map((b) => <li key={b}>{b}</li>)}</ul>
    </div>
  ) : (
    <div className="card-flat p-3 border-green-300 bg-green-50 text-[12.5px] text-green-800 flex items-center gap-1.5" data-testid="hr-ready">
      <CheckCircle size={15} /> All mandatory documents and family contacts are complete — Joining Letter can be generated.
    </div>
  )
);

const Exceptions = ({ items, isAdmin, onChanged }) => {
  if (!items.length) return null;
  const revoke = async (id) => {
    try { await api.delete(`/hr/exceptions/${id}`); toast.success("Exception revoked"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <Section title="Admin Exceptions" testid="hr-exceptions">
      <div className="card-flat divide-y divide-border">
        {items.map((e) => (
          <div key={e.id} className="p-3 text-[12.5px] flex justify-between gap-2" data-testid={`hr-exception-${e.id}`}>
            <span><b>{e.item_key}</b> — {e.reason} <span className="text-muted-foreground">({e.by}, {fmtDate(e.at)})</span></span>
            {isAdmin && <Button size="sm" variant="ghost" className="h-7 text-destructive" onClick={() => revoke(e.id)}>Revoke</Button>}
          </div>
        ))}
      </div>
    </Section>
  );
};

const AuditLog = ({ rows }) => (
  <Section title="History & Audit Log" testid="hr-audit">
    <div className="card-flat max-h-72 overflow-y-auto divide-y divide-border">
      {rows.length === 0 && <div className="p-3 text-[12px] text-muted-foreground">No activity yet.</div>}
      {rows.map((a) => (
        <div key={a.id} className="p-2.5 text-[12px] flex gap-3">
          <span className="font-mono text-muted-foreground whitespace-nowrap">{new Date(a.at).toLocaleString("en-IN")}</span>
          <span><b>{a.action.replace(/_/g, " ")}</b> — {a.detail} <span className="text-muted-foreground">by {a.by}</span></span>
        </div>
      ))}
    </div>
  </Section>
);

const STEP_LABELS = ["Open Employee Mgmt", "Employee Details", "Upload Documents", "Family Contacts", "Salary Details", "Verify & Save", "Joining Letter"];

const Steps = ({ p }) => {
  const mand = p.checklist.filter((c) => c.mandatory);
  const e = p.employee;
  const uploaded = mand.every((c) => c.excepted || ["pending_verification", "verified", "rejected"].includes(c.status));
  const verified = mand.every((c) => c.excepted || c.status === "verified");
  const family = p.family.every((f) => f.complete || f.excepted);
  const details = !!(e.name && e.phone && e.designation && e.joining_date && e.employment_type);
  const salary = Number(e.monthly_salary) > 0;
  const letter = p.letters.length > 0;
  const done = [true, details, uploaded, family, salary, verified, letter];
  return (
    <ol className="grid grid-cols-2 md:grid-cols-7 gap-2" data-testid="hr-steps">
      {STEP_LABELS.map((l, i) => (
        <li key={l} data-testid={`hr-step-${i + 1}`}
          className={`card-flat px-3 py-2 text-[11.5px] flex items-center gap-2 ${done[i] ? "border-green-300 bg-green-50 text-green-800" : "text-muted-foreground"}`}>
          <span className={`w-5 h-5 shrink-0 rounded-full flex items-center justify-center text-[10px] font-bold ${done[i] ? "bg-green-600 text-white" : "bg-muted"}`}>{done[i] ? "✓" : i + 1}</span>
          {l}
        </li>
      ))}
    </ol>
  );
};

export default function EmployeeHrProfile() {
  const { eid } = useParams();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canVerify = isAdmin || user?.role === "regional_manager";
  const back = isAdmin ? "/hrms" : "/my-hrms";
  const [p, setP] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    try { setP((await api.get(`/hr/employees/${eid}`)).data); }
    catch (e) { setErr(formatApiError(e)); }
  }, [eid]);
  useEffect(() => { load(); }, [load]);

  if (err && !p) return <div className="page-shell text-sm text-destructive" data-testid="hr-profile-error">{err} — <Link to={back} className="underline">back to HRMS</Link></div>;
  if (!p) return <div className="page-shell text-sm text-muted-foreground">Loading…</div>;
  const emp = p.employee;

  return (
    <div className="page-shell space-y-6" data-testid="hr-profile-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to={back} className="text-[12px] text-muted-foreground inline-flex items-center gap-1 hover:text-foreground" data-testid="hr-back"><ArrowLeft size={12} /> HRMS → Employees</Link>
          <div className="eyebrow mt-2">Employee Documents & Joining</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1" data-testid="hr-emp-name">{emp.name}</h1>
          <p className="text-sm text-muted-foreground"><span className="font-mono" data-testid="hr-emp-code">{emp.employee_code}</span> · {emp.designation || "No designation"}{emp.phone && ` · ${emp.phone}`}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {isAdmin && <DocTypesDialog onSaved={load} />}
          <GenerateLetterButton emp={emp} canGenerate={p.can_generate} blockers={p.blockers} onDone={load} />
        </div>
      </div>

      <Blockers blockers={p.blockers} />
      <Steps p={p} />

      <Section title="Step 2 · Employee Details" testid="hr-details-wrap">
        <DetailsEditor emp={emp} onSaved={load} />
      </Section>

      <Section title="Step 3 · Upload Required Documents (Step 6 · Verify: Regional Manager / Admin)" testid="hr-docs-section">
        <DocChecklist eid={eid} checklist={p.checklist} isAdmin={isAdmin} canVerify={canVerify} onChanged={load} />
      </Section>

      <Section title="Step 4 · Family Contacts" testid="hr-family-wrap">
        <FamilyContacts eid={eid} family={p.family} isAdmin={isAdmin} onChanged={load} />
      </Section>

      <Section title="Step 5 · Salary Details" testid="hr-salary-wrap">
        <SalaryDetails emp={emp} canEdit={canVerify} onSaved={load} />
      </Section>

      <Section title="Salary Increments" testid="hr-increments-wrap">
        <IncrementsSection emp={emp} isAdmin={isAdmin} onChanged={load} />
      </Section>

      <Exceptions items={p.exceptions} isAdmin={isAdmin} onChanged={load} />

      <Section title="Face Attendance" testid="hr-face-wrap">
        <FaceSetup eid={eid} checklist={p.checklist} />
      </Section>

      <Section title="Step 7 · Joining Letters" testid="hr-letters">
        <div className="card-flat divide-y divide-border">
          {p.letters.length === 0 && <div className="p-3 text-[12px] text-muted-foreground">No joining letter generated yet.</div>}
          {p.letters.map((L) => (
            <div key={L.id} className="p-3 flex items-center justify-between flex-wrap gap-2 text-[12.5px]" data-testid={`hr-letter-${L.id}`}>
              <span><b className="font-mono">{L.letter_no}</b> · Joining {fmtDate(L.joining_date)} · {L.designation} <span className="text-muted-foreground">by {L.created_by}, {fmtDate(L.created_at)}</span>
                {(L.emails || []).length > 0 && <span className="block text-[11px] text-green-700" data-testid={`hr-letter-sent-${L.id}`}>Emailed to {L.emails[L.emails.length - 1].to} on {fmtDate(L.emails[L.emails.length - 1].at)}</span>}
              </span>
              <div className="flex items-center gap-1">
                <LetterActions letter={L} empEmail={emp.email} onSent={load} />
                {isAdmin && (
                  <Button size="sm" variant="ghost" className="h-7 px-2 text-[11px] text-destructive" data-testid={`hr-letter-delete-${L.id}`}
                    onClick={async () => {
                      if (!window.confirm(`Delete joining letter ${L.letter_no}? If it is the latest letter, the next letter generated will reuse ${L.letter_no}.`)) return;
                      try { await api.delete(`/hr/letters/${L.id}`); toast.success("Letter deleted"); load(); }
                      catch (e) { toast.error(formatApiError(e)); }
                    }}>Delete</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      <AuditLog rows={p.audit} />
    </div>
  );
}
